import { createFileRoute, Link } from '@tanstack/react-router'
import { createConnectQueryKey, useMutation, useQuery } from '@connectrpc/connect-query'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { toast } from 'sonner'
import { ArrowLeft, ExternalLink, Loader2, RefreshCw } from 'lucide-react'
import {
  adminGetOrganizationBilling,
  adminRevokeAccessOverride,
  adminRevokeRateException,
  adminResyncBillingState,
  adminResyncSeats,
} from '@/gen/cortado/v1/admin-AdminService_connectquery'
import type { AccessOverride, OrgBillingDetail, RateException } from '@/gen/cortado/v1/admin_pb'
import { BillingAccessStateEnum } from '@/gen/cortado/v1/subscriptions_pb'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { SetBillingStartDateDialog, ResetTrialEligibilityDialog } from '@/components/billing/trial-billing-start-dialogs'
import { AssignPlanDialog } from '@/components/billing/assign-plan-dialog'
import { AccessOverrideDialog } from '@/components/billing/access-override-dialog'
import { ForceCancelDialog } from '@/components/billing/force-cancel-dialog'
import {
  billingIntervalLabel,
  billingStateBadgeVariant,
  billingStateLabel,
  canSetBillingStartDate,
  formatDateTime,
  planKeyLabel,
  stripeBillingIdentifiers,
  stripeCustomerDashboardUrl,
  stripeSubscriptionDashboardUrl,
  type BadgeVariant,
} from '@/lib/billing'

export const Route = createFileRoute('/_admin/billing/$organizationId')({
  component: OrgBillingDetailPage,
})

const RATE_EXCEPTION_STATUS_BADGE: Record<string, BadgeVariant> = {
  PENDING: 'warning',
  APPLIED: 'success',
  EXPIRED: 'secondary',
  REVOKED: 'danger',
  FAILED: 'danger',
}

const OVERRIDE_KIND_BADGE: Record<string, BadgeVariant> = {
  comp: 'purple',
  whitelist: 'info',
  suspend: 'danger',
}

function detailInvalidateKey() {
  return createConnectQueryKey({ schema: adminGetOrganizationBilling, cardinality: undefined })
}

function OrgBillingDetailPage() {
  const { organizationId } = Route.useParams()
  const queryClient = useQueryClient()

  const { data, isLoading, isError, error } = useQuery(adminGetOrganizationBilling, { organizationId })
  const billing = data?.billing
  const state = billing?.billingState

  const invalidate = () => queryClient.invalidateQueries({ queryKey: detailInvalidateKey() })

  const [setBillingStartOpen, setSetBillingStartOpen] = useState(false)
  const [resetTrialOpen, setResetTrialOpen] = useState(false)
  const [assignPlanOpen, setAssignPlanOpen] = useState(false)
  const [accessOverrideOpen, setAccessOverrideOpen] = useState(false)
  const [forceCancelOpen, setForceCancelOpen] = useState(false)
  const [revokeException, setRevokeException] = useState<RateException | null>(null)
  const [revokeOverride, setRevokeOverride] = useState<AccessOverride | null>(null)

  const resyncStateMutation = useMutation(adminResyncBillingState, {
    onSuccess: () => {
      toast.success('Billing state resynced from Stripe')
      invalidate()
    },
    onError: (err) => toast.error(err.message || 'Failed to resync billing state'),
  })

  const resyncSeatsMutation = useMutation(adminResyncSeats, {
    onSuccess: (res) => {
      toast.success(`Seats resynced — desired ${res.desiredSeats}, applied ${res.appliedSeats}`)
      invalidate()
    },
    onError: (err) => toast.error(err.message || 'Failed to resync seats'),
  })

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    )
  }

  if (isError || !billing || !state) {
    return (
      <div className="space-y-6">
        <Link to="/billing" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" />
          Back to Billing
        </Link>
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
          {error instanceof Error ? error.message : 'Unable to load billing detail for this organization'}
        </div>
      </div>
    )
  }

  const canManageTrialClock = canSetBillingStartDate(billing)
  const { customerId: stripeCustomerId, subscriptionId: stripeSubscriptionId } =
    stripeBillingIdentifiers(billing)
  const customerUrl = stripeCustomerId ? stripeCustomerDashboardUrl(stripeCustomerId) : undefined
  const subscriptionUrl = stripeSubscriptionId
    ? stripeSubscriptionDashboardUrl(stripeSubscriptionId)
    : undefined

  return (
    <div className="space-y-8">
      <div className="space-y-3">
        <Link to="/billing" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="h-4 w-4" />
          Back to Billing
        </Link>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 flex-wrap items-center gap-3">
            <h1 className="min-w-0 text-2xl font-bold">{billing.organizationName}</h1>
            <Badge variant={billingStateBadgeVariant(state.state)}>{billingStateLabel(state.state)}</Badge>
            {state.isSuspended && <Badge variant="danger">Suspended</Badge>}
            {billing.chargebackFlagged && (
              <Badge
                variant="danger"
                title={
                  billing.lastChargebackAt
                    ? `Last chargeback: ${formatDateTime(billing.lastChargebackAt)}`
                    : undefined
                }
              >
                Chargeback (DEC-45)
              </Badge>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            className="w-full sm:w-auto"
            disabled={resyncStateMutation.isPending}
            onClick={() => resyncStateMutation.mutate({ organizationId })}
          >
            {resyncStateMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Resync billing state
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Billing state</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            <InfoRow label="State" value={billingStateLabel(state.state)} />
            <InfoRow label="Since" value={formatDateTime(state.stateSince)} />
            <InfoRow label="Read-only until" value={formatDateTime(state.readOnlyUntil)} />
            <InfoRow label="Grace ends" value={formatDateTime(state.graceEndsAt)} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Plan &amp; trial</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            <InfoRow label="Assigned plan" value={planKeyLabel(state.assignedPlanKey)} />
            <InfoRow label="Choosable" value={state.planIsAssigned ? 'No — assigned' : 'Yes'} />
            <InfoRow label="Interval" value={billingIntervalLabel(state.selectedInterval)} />
            <InfoRow label="Trial available" value={state.trialAvailable ? 'Yes' : 'No'} />
            <InfoRow label="Trial started" value={formatDateTime(state.trialStartedAt)} />
            <InfoRow label="First charge" value={formatDateTime(state.firstChargeAt)} />
            <InfoRow label="Charge amount" value={state.firstChargeAmountFormatted || '—'} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-medium text-muted-foreground">Seats</CardTitle>
            <Button
              variant="ghost"
              size="sm"
              disabled={resyncSeatsMutation.isPending}
              onClick={() => resyncSeatsMutation.mutate({ organizationId })}
            >
              {resyncSeatsMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Resync'}
            </Button>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            <InfoRow label="Included" value={String(state.includedSeats)} />
            <InfoRow label="Billable" value={String(state.billableSeats)} />
            <InfoRow
              label="Pending reduction"
              value={state.pendingSeatReductionTo !== undefined ? String(state.pendingSeatReductionTo) : '—'}
            />
            <InfoRow label="Seat payment" value={state.seatPaymentFailed ? 'Failed' : 'OK'} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Stripe</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <StripeIdRow label="Customer" id={stripeCustomerId} url={customerUrl} />
            <StripeIdRow label="Subscription" id={stripeSubscriptionId} url={subscriptionUrl} />
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-wrap gap-2">
        <span title={canManageTrialClock ? undefined : 'Only available while the Stripe subscription is trialing'}>
          <Button variant="outline" size="sm" disabled={!canManageTrialClock} onClick={() => setSetBillingStartOpen(true)}>
            Set billing start date
          </Button>
        </span>
        <Button variant="outline" size="sm" onClick={() => setResetTrialOpen(true)}>
          Reset trial eligibility
        </Button>
        <Button variant="outline" size="sm" onClick={() => setAssignPlanOpen(true)}>
          Assign plan
        </Button>
        <Button variant="outline" size="sm" onClick={() => setAccessOverrideOpen(true)}>
          Add access override
        </Button>
        <Button variant="destructive" size="sm" onClick={() => setForceCancelOpen(true)}>
          Force cancel
        </Button>
      </div>

      <RateExceptionsSection billing={billing} onRevoke={setRevokeException} />
      <AccessOverridesSection billing={billing} onRevoke={setRevokeOverride} />
      <ReminderLogSection billing={billing} />
      <StateTimelineSection billing={billing} />
      <SeatHistorySection billing={billing} />

      <SetBillingStartDateDialog
        organizationId={organizationId}
        organizationName={billing.organizationName}
        currentFirstChargeAt={state.firstChargeAt}
        open={setBillingStartOpen}
        onOpenChange={setSetBillingStartOpen}
        onSuccess={invalidate}
      />
      <ResetTrialEligibilityDialog
        organizationId={organizationId}
        organizationName={billing.organizationName}
        open={resetTrialOpen}
        onOpenChange={setResetTrialOpen}
        onSuccess={invalidate}
      />
      <AssignPlanDialog
        organizationId={organizationId}
        organizationName={billing.organizationName}
        currentPlanKey={state.assignedPlanKey}
        open={assignPlanOpen}
        onOpenChange={setAssignPlanOpen}
        onSuccess={invalidate}
      />
      <AccessOverrideDialog
        organizationId={organizationId}
        organizationName={billing.organizationName}
        open={accessOverrideOpen}
        onOpenChange={setAccessOverrideOpen}
        onSuccess={invalidate}
      />
      <ForceCancelDialog
        organizationId={organizationId}
        organizationName={billing.organizationName}
        open={forceCancelOpen}
        onOpenChange={setForceCancelOpen}
        onSuccess={invalidate}
      />
      {revokeException && (
        <RevokeRateExceptionDialog
          exception={revokeException}
          open
          onOpenChange={(open) => !open && setRevokeException(null)}
          onSuccess={invalidate}
        />
      )}
      {revokeOverride && (
        <RevokeAccessOverrideDialog
          override={revokeOverride}
          open
          onOpenChange={(open) => !open && setRevokeOverride(null)}
          onSuccess={invalidate}
        />
      )}
    </div>
  )
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-2">
      <span className="text-muted-foreground">{label}</span>
      <span className="max-w-full break-all text-right font-medium">{value}</span>
    </div>
  )
}

function StripeIdRow({ label, id, url }: { label: string; id?: string; url?: string }) {
  if (!id) {
    return (
      <div className="flex flex-wrap items-start justify-between gap-4">
        <span className="text-muted-foreground">{label}</span>
        <span className="max-w-full break-all text-right font-medium text-muted-foreground">—</span>
      </div>
    )
  }
  return (
    <div className="flex flex-wrap items-start justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      {url ? (
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex max-w-full items-center gap-1 break-all font-mono text-xs hover:underline"
        >
          {id}
          <ExternalLink className="h-3 w-3" />
        </a>
      ) : (
        <span className="font-mono text-xs">{id}</span>
      )}
    </div>
  )
}

function RateExceptionsSection({
  billing,
  onRevoke,
}: {
  billing: OrgBillingDetail
  onRevoke: (exception: RateException) => void
}) {
  const rows = billing.rateExceptions
  return (
    <div className="space-y-3">
      <h2 className="text-lg font-semibold">Rate &amp; trial exceptions</h2>
      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Kind</TableHead>
              <TableHead>Approved rate / days</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Effective</TableHead>
              <TableHead>Approver</TableHead>
              <TableHead className="w-20 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!rows.length ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground py-6">
                  No exceptions on file
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    <Badge variant={row.exceptionKind === 'trial' ? 'info' : 'purple'}>{row.exceptionKind}</Badge>
                  </TableCell>
                  <TableCell>
                    {row.exceptionKind === 'trial' ? `${row.approvedTrialDays ?? '—'}d` : row.approvedRateDescription}
                  </TableCell>
                  <TableCell className="max-w-xs truncate text-muted-foreground" title={row.reason}>
                    {row.reason}
                  </TableCell>
                  <TableCell>
                    <Badge variant={RATE_EXCEPTION_STATUS_BADGE[row.status] ?? 'secondary'}>{row.status}</Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{formatDateTime(row.effectiveDate)}</TableCell>
                  <TableCell className="text-muted-foreground">{row.approverName}</TableCell>
                  <TableCell className="text-right">
                    {row.status === 'APPLIED' || row.status === 'PENDING' ? (
                      <Button variant="outline" size="sm" onClick={() => onRevoke(row)}>
                        Revoke
                      </Button>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

function AccessOverridesSection({
  billing,
  onRevoke,
}: {
  billing: OrgBillingDetail
  onRevoke: (override: AccessOverride) => void
}) {
  const rows = billing.accessOverrides
  return (
    <div className="space-y-3">
      <h2 className="text-lg font-semibold">Access overrides</h2>
      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Kind</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead>Starts</TableHead>
              <TableHead>Ends</TableHead>
              <TableHead>Revoked</TableHead>
              <TableHead className="w-20 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!rows.length ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-6">
                  No overrides on file
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    <Badge variant={OVERRIDE_KIND_BADGE[row.overrideKind] ?? 'secondary'}>{row.overrideKind}</Badge>
                  </TableCell>
                  <TableCell className="max-w-xs truncate text-muted-foreground" title={row.reason}>
                    {row.reason}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{formatDateTime(row.startsAt)}</TableCell>
                  <TableCell className="text-muted-foreground">{formatDateTime(row.endsAt) === '—' ? 'Indefinite' : formatDateTime(row.endsAt)}</TableCell>
                  <TableCell className="text-muted-foreground">{formatDateTime(row.revokedAt)}</TableCell>
                  <TableCell className="text-right">
                    {!row.revokedAt ? (
                      <Button variant="outline" size="sm" onClick={() => onRevoke(row)}>
                        Revoke
                      </Button>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

function ReminderLogSection({ billing }: { billing: OrgBillingDetail }) {
  const rows = billing.reminderLog
  return (
    <div className="space-y-3">
      <h2 className="text-lg font-semibold">Reminder-send log</h2>
      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Reminder</TableHead>
              <TableHead>Scheduled for</TableHead>
              <TableHead>Sent</TableHead>
              <TableHead>Skipped</TableHead>
              <TableHead>Superseded</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!rows.length ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground py-6">
                  No reminders sent yet
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="font-mono text-xs">{row.reminderKey}</TableCell>
                  <TableCell className="text-muted-foreground">{formatDateTime(row.scheduledFor)}</TableCell>
                  <TableCell className="text-muted-foreground">{formatDateTime(row.sentAt)}</TableCell>
                  <TableCell className="text-muted-foreground">{row.skipReason ?? '—'}</TableCell>
                  <TableCell className="text-muted-foreground">{row.supersededReason ?? '—'}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

function StateTimelineSection({ billing }: { billing: OrgBillingDetail }) {
  const rows = billing.stateTimeline
  return (
    <div className="space-y-3">
      <h2 className="text-lg font-semibold">State timeline</h2>
      {!rows.length ? (
        <p className="text-muted-foreground text-sm">No recorded state changes</p>
      ) : (
        <ol className="space-y-3 border-l pl-4">
          {rows.map((row, i) => (
            <li key={i}>
              <div className="flex flex-wrap items-center gap-2">
                {row.fromState !== BillingAccessStateEnum.BILLING_ACCESS_STATE_UNSPECIFIED && (
                  <>
                    <Badge variant={billingStateBadgeVariant(row.fromState)}>
                      {billingStateLabel(row.fromState)}
                    </Badge>
                    <span className="text-muted-foreground text-xs">&rarr;</span>
                  </>
                )}
                <Badge variant={billingStateBadgeVariant(row.toState)}>{billingStateLabel(row.toState)}</Badge>
              </div>
              <p className="text-muted-foreground text-xs">
                {formatDateTime(row.occurredAt)} &middot; {row.source}
              </p>
            </li>
          ))}
        </ol>
      )}
    </div>
  )
}

function SeatHistorySection({ billing }: { billing: OrgBillingDetail }) {
  const rows = billing.seatHistory
  return (
    <div className="space-y-3">
      <h2 className="text-lg font-semibold">Seat history</h2>
      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Occurred</TableHead>
              <TableHead>Desired</TableHead>
              <TableHead>Applied</TableHead>
              <TableHead>Source</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!rows.length ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-muted-foreground py-6">
                  No recorded seat changes
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, i) => (
                <TableRow key={i}>
                  <TableCell className="text-muted-foreground">{formatDateTime(row.occurredAt)}</TableCell>
                  <TableCell>{row.desiredSeats}</TableCell>
                  <TableCell>{row.appliedSeats}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{row.source}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

function RevokeRateExceptionDialog({
  exception,
  open,
  onOpenChange,
  onSuccess,
}: {
  exception: RateException
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
}) {
  const [reason, setReason] = useState('')
  const mutation = useMutation(adminRevokeRateException, {
    onSuccess: () => {
      toast.success('Exception revoked')
      onSuccess()
      onOpenChange(false)
    },
    onError: (err) => toast.error(err.message || 'Failed to revoke exception'),
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Revoke {exception.exceptionKind} exception?</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid gap-2">
            <Label htmlFor="revoke-exception-reason-a2">Reason</Label>
            <Textarea id="revoke-exception-reason-a2" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} required />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={!reason.trim() || mutation.isPending}
              onClick={() => mutation.mutate({ id: exception.id, reason: reason.trim() })}
            >
              {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Revoke
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function RevokeAccessOverrideDialog({
  override,
  open,
  onOpenChange,
  onSuccess,
}: {
  override: AccessOverride
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
}) {
  const mutation = useMutation(adminRevokeAccessOverride, {
    onSuccess: () => {
      toast.success('Override revoked')
      onSuccess()
      onOpenChange(false)
    },
    onError: (err) => toast.error(err.message || 'Failed to revoke override'),
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Revoke {override.overrideKind} override?</DialogTitle>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate({ overrideId: override.id })}
          >
            {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Revoke
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
